import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import { useTracking } from '../utils';
import { Close, Error, Info, ThumbUp, Warn } from '@gjensidige/builders-icons';
import type { TrackingProp } from '../types';
import styles from './alert.module.css';
import clsx from 'clsx';

export type AlertProps = React.ComponentPropsWithoutRef<'div'> & {
  align?: 'center' | 'left' | 'right';
  closeLabel?: string;
  onClose?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  icon?: boolean;
  severity?: 'success' | 'informative' | 'warning' | 'error';
  size?: 'md' | 'sm';
  tracking?: TrackingProp;
  variant?: 'filled' | 'outlined';
};

const Icons = {
  error: Error,
  informative: Info,
  success: ThumbUp,
  warning: Warn
};

export const Alert = forwardRef<HTMLDivElement, AlertProps>(function Alert(
  {
    align = 'center',
    children,
    className,
    closeLabel,
    icon = true,
    onClose,
    severity = 'informative',
    size = 'md',
    tracking = false,
    variant = 'filled',
    ...rest
  },
  ref
) {
  const innerRef = useRef<HTMLDivElement>(null);
  const isTracked = useRef(false); // Using useRef as it does not trigger re-render
  const [closed, setClosed] = useState(false);
  const hasEventLabel = typeof tracking === 'object' && !!tracking.eventLabel;
  const track = useTracking(hasEventLabel && tracking);
  const Icon = Icons[severity];

  const handleClose = (event: React.MouseEvent<HTMLButtonElement>) => {
    track({
      eventCategory: `alert_${severity}`,
      eventAction: 'close'
    });
    setClosed(true);
    onClose?.(event); // Track before onClick in case URL changes
  };

  useImperativeHandle(ref, () => innerRef.current as HTMLDivElement); // Forward innerRef

  // Only track first impression
  useEffect(() => {
    if (!isTracked.current && (isTracked.current = true))
      track({
        eventCategory: `alert_${severity}`,
        eventAction: 'impression'
      });
  }, [isTracked, track, severity]);

  return (
    <div
      {...rest}
      className={clsx(
        className,
        styles.alert,
        styles[severity],
        styles[size],
        styles[variant]
      )}
      hidden={closed}
      ref={innerRef}
    >
      <div className={clsx(styles.content, styles[`align-${align}`])}>
        {icon && <Icon className={styles.icon} />}
        {children}
      </div>
      {closeLabel && (
        <button
          aria-label={closeLabel}
          className={styles.close}
          onClick={handleClose}
          type="button"
        >
          <Close />
        </button>
      )}
    </div>
  );
});
